// Pure GeoJSON builder for the Place Selection Builder → Job Request sync.
//
// This module is intentionally free of any Vue/component, store, or mapbox-gl
// dependency so that fast-check property tests can import and exercise it
// directly, mirroring the extraction pattern used by
// placeSelectionBuilderHelpers.js.
//
// Feature: builder-jobrequest-sync (Task 1)
//
// It maps a Builder_Place (as normalized into placeSelection.selectedPlaces) to
// a single GeoJSON Feature whose properties follow the shape the downstream
// fixGeoJSON / fixPropertiesName path expects, so those steps leave the Feature
// intact.

import {
  placeKey,
  sourceType,
  hasResolvableLocation,
} from './placeSelectionBuilderHelpers';

// Re-export the shared helpers so callers (and tests) can import the full
// builder vocabulary from a single module. These are imported (not duplicated)
// from placeSelectionBuilderHelpers so the mapping stays defined in one place.
export { placeKey, sourceType, hasResolvableLocation };

/**
 * Derive the GeoJSON geometry for a Builder_Place, by precedence:
 *   1. `place.geometry` (Point / Polygon / MultiPolygon) — used directly.
 *   2. `place.centerLngLat` (`[lng, lat]`) — wrapped as a Point.
 *   3. First `place.previewFeatures[i].geometry` of type `Point` (csv uploads).
 *   4. Otherwise `null`.
 *
 * @param {Object} place - The Builder_Place.
 * @returns {Object|null} A GeoJSON geometry object, or null when none derivable.
 */
export function deriveGeometry(place) {
  if (!place) return null;

  // (1) A top-level geometry object is used directly.
  const geometry = place.geometry;
  if (geometry && typeof geometry === 'object') {
    const type = geometry.type;
    if (type === 'Point' || type === 'Polygon' || type === 'MultiPolygon') {
      return geometry;
    }
  }

  // (2) A center point [lng, lat] is wrapped as a Point geometry.
  const center = place.centerLngLat;
  if (
    Array.isArray(center) &&
    center.length === 2 &&
    typeof center[0] === 'number' &&
    typeof center[1] === 'number'
  ) {
    return { type: 'Point', coordinates: center };
  }

  // (3) csv uploads: the first previewFeatures Point geometry.
  const features = place.previewFeatures;
  if (Array.isArray(features)) {
    for (let i = 0; i < features.length; i += 1) {
      const feature = features[i];
      const featureGeometry = feature && feature.geometry;
      if (featureGeometry && featureGeometry.type === 'Point') {
        return featureGeometry;
      }
    }
  }

  // (4) Nothing derivable.
  return null;
}

/**
 * Compute `[longitude, latitude]` from a derived geometry, for the Feature's
 * `longitude` / `latitude` properties.
 *
 * - Point: its coordinates.
 * - Polygon: the first coordinate of the outer ring.
 * - MultiPolygon: the first coordinate of the first polygon's outer ring.
 *
 * Returns `[undefined, undefined]` when a representative point cannot be found.
 *
 * @param {Object} geometry - A GeoJSON geometry.
 * @returns {[number|undefined, number|undefined]} `[lng, lat]`.
 */
function representativeLngLat(geometry) {
  if (!geometry || typeof geometry !== 'object') return [undefined, undefined];
  const { type, coordinates } = geometry;

  if (type === 'Point') {
    if (Array.isArray(coordinates) && coordinates.length >= 2) {
      return [coordinates[0], coordinates[1]];
    }
    return [undefined, undefined];
  }

  if (type === 'Polygon') {
    const ring = Array.isArray(coordinates) ? coordinates[0] : undefined;
    const point = Array.isArray(ring) ? ring[0] : undefined;
    if (Array.isArray(point) && point.length >= 2) {
      return [point[0], point[1]];
    }
    return [undefined, undefined];
  }

  if (type === 'MultiPolygon') {
    const polygon = Array.isArray(coordinates) ? coordinates[0] : undefined;
    const ring = Array.isArray(polygon) ? polygon[0] : undefined;
    const point = Array.isArray(ring) ? ring[0] : undefined;
    if (Array.isArray(point) && point.length >= 2) {
      return [point[0], point[1]];
    }
    return [undefined, undefined];
  }

  return [undefined, undefined];
}

/**
 * Map one Builder_Place to a GeoJSON Feature.
 *
 * Geometry is resolved from `place.geometry`, else a Point built from
 * `place.centerLngLat`, else (csv) the first `previewFeatures` Point. The
 * Feature's `id` is `'<name>|<Place_Key>'`, and its `properties` carry
 * `polygon_id` (Place_Key), `name`, `latitude`, `longitude`, `categories`, and
 * `address`, so the downstream `fixGeoJSON` / `fixPropertiesName` path (which
 * keys off a non-empty `name`) leaves it intact.
 *
 * @param {Object} place - The Builder_Place.
 * @returns {Object|null} The GeoJSON Feature, or null when no geometry can be
 *   derived (the place is unresolvable and should be excluded by callers).
 */
export function builderPlaceToFeature(place) {
  if (!place) return null;

  const geometry = deriveGeometry(place);
  if (geometry === null) return null;

  const key = placeKey(place);
  const name = place.name;
  const [longitude, latitude] = representativeLngLat(geometry);

  return {
    id: `${name}|${key}`,
    type: 'Feature',
    geometry,
    properties: {
      polygon_id: key,
      name,
      latitude,
      longitude,
      categories: place.categories,
      address: place.address,
    },
  };
}

/**
 * Build a single combined GeoJSON FeatureCollection from a Builder_List.
 *
 * The Builder_List is iterated in order and each place is classified:
 *   - No determinable Place_Key (`placeKey(place) === null`) → excluded and
 *     collected into `excludedNoKey` (Req 4.4).
 *   - Not resolvable (`hasResolvableLocation(place)` is false) OR no Feature can
 *     be derived (`builderPlaceToFeature(place)` returns `null`) → excluded and
 *     collected into `excludedUnresolvable` (Req 6.1, 6.3).
 *   - Otherwise the place is a candidate. Candidates are de-duplicated by
 *     Place_Key keeping the lowest-index (first) occurrence; later occurrences
 *     of an already-seen key are silently dropped (Req 4.1, 4.2).
 *
 * The emitted `features` preserve first-occurrence order of the retained places
 * in Builder_List order (Req 3.4). The input array is never mutated.
 *
 * @param {Array<Object>} places - The Builder_List
 *   (`placeSelection.selectedPlaces`).
 * @returns {{
 *   featureCollection: { type: 'FeatureCollection', features: Array<Object> },
 *   included: Array<Object>,
 *   excludedUnresolvable: Array<Object>,
 *   excludedNoKey: Array<Object>
 * }} The combined FeatureCollection plus the retained places (`included`) and
 *   the two exclusion sets.
 */
export function buildBuilderFeatureCollection(places) {
  const list = Array.isArray(places) ? places : [];

  const features = [];
  const included = [];
  const excludedUnresolvable = [];
  const excludedNoKey = [];

  const seenKeys = new Set();

  for (let i = 0; i < list.length; i += 1) {
    const place = list[i];

    // (Req 4.4) No determinable Place_Key → excluded, recorded separately.
    const key = placeKey(place);
    if (key === null) {
      excludedNoKey.push(place);
      continue;
    }

    // (Req 6.1/6.3) Unresolvable location or no derivable Feature → excluded.
    if (!hasResolvableLocation(place)) {
      excludedUnresolvable.push(place);
      continue;
    }
    const feature = builderPlaceToFeature(place);
    if (feature === null) {
      excludedUnresolvable.push(place);
      continue;
    }

    // (Req 4.1, 4.2) De-duplicate by Place_Key, keeping the first occurrence.
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);

    // (Req 3.4) Preserve first-occurrence Builder_List order.
    features.push(feature);
    included.push(place);
  }

  return {
    featureCollection: { type: 'FeatureCollection', features },
    included,
    excludedUnresolvable,
    excludedNoKey,
  };
}

/**
 * Decide whether a Job_Request_Sync is in scope.
 *
 * The sync only runs within the Dataset_Flow (an active route path containing
 * the `/dataset/` segment) AND while Experience_Mode is the Place_Selection_Builder
 * (`experienceMode === 'new'`). A route path that is null, empty, or otherwise
 * unresolved is treated as out of scope, so the caller performs no sync and
 * leaves the Job_Request untouched.
 *
 * @param {string|null|undefined} routePath - The active route path
 *   (`this.$route.path`), or a null/empty/unresolved value.
 * @param {string|null|undefined} experienceMode - The Experience_Mode mirror
 *   from the `placeSelection` slice (`'new'` for Builder, `'old'` for Classic).
 * @returns {boolean} True only when the sync is in scope (Dataset_Flow +
 *   Builder mode).
 */
export function isSyncInScope(routePath, experienceMode) {
  const inDatasetFlow =
    typeof routePath === 'string' && routePath.includes('/dataset/');
  const inBuilderMode = experienceMode === 'new';
  return inDatasetFlow && inBuilderMode;
}

/**
 * Pure reducer for the place-related Job_Request write set.
 *
 * Given the prior `jobRequest`-shaped object, a built GeoJSON
 * `featureCollection`, and the out-of-scope guard inputs (route path +
 * experienceMode), this returns the *next* `jobRequest` object. It is a pure
 * function: it never mutates its inputs, performs no store/IndexedDB writes,
 * and derives every value from its arguments alone. The mixin is responsible
 * for applying the decision via commits/dispatches (including the actual
 * `savePolygonString` / `deletePolygonString` side effects); this reducer only
 * decides the resulting field values.
 *
 * Scope gate (Req 8.1, 8.2, 8.4): when the sync is out of scope — the route
 * path does not contain `/dataset/` (including null/empty/unresolved) OR
 * Experience_Mode is not Builder (`'new'`) — the prior `jobRequest` object is
 * returned unchanged (the same reference), so no Job_Request field changes.
 *
 * Touched fields (Req 7.1, 7.2, 7.3) — and ONLY these:
 *   - `selectedLocationMethod`: `'geojson'` when the FeatureCollection has at
 *     least one Feature, otherwise the default `'select'` (Req 2.1, 2.2, 2.3).
 *   - `polygonInputOptions.polygonFormat`: set to `'GEOJSON'` via a shallow
 *     merge onto the prior `polygonInputOptions`, preserving all other keys
 *     (notably `polygonNameAliasElement`) and never introducing `geojson`.
 *   - `polygonStringRef`: left as the prior value here; the reducer instead
 *     reports the intended Polygon_Storage side effect via `savePolygonString`
 *     / `deletePolygonString` (see the returned `polygonAction`) so the mixin
 *     can dispatch it, which is what actually mutates `polygonStringRef`.
 *
 * Every other `jobRequest` field is copied byte-for-byte from the prior object
 * (Req 7.2): the result is a shallow clone of the prior object with only the
 * touched fields overwritten.
 *
 * @param {Object} priorJobRequest - The prior `jobRequest`-shaped object.
 * @param {{ type: string, features: Array<Object> }} featureCollection - The
 *   FeatureCollection produced by `buildBuilderFeatureCollection`.
 * @param {Object} scope - The out-of-scope guard inputs.
 * @param {string|null|undefined} scope.routePath - The active route path.
 * @param {string|null|undefined} scope.experienceMode - The Experience_Mode.
 * @returns {{
 *   jobRequest: Object,
 *   polygonAction: 'save' | 'delete' | 'none',
 *   polygonString: string | null,
 *   inScope: boolean
 * }} The next `jobRequest` object plus the intended Polygon_Storage side
 *   effect (`polygonAction` = `save` with `polygonString` set when non-empty,
 *   `delete` when empty, `none` when out of scope) and the `inScope` flag.
 */
export function reduceJobRequest(priorJobRequest, featureCollection, scope) {
  const prior = priorJobRequest || {};
  const { routePath, experienceMode } = scope || {};

  // (Req 8.1, 8.2, 8.4) Out of scope → return the prior jobRequest unchanged.
  if (!isSyncInScope(routePath, experienceMode)) {
    return {
      jobRequest: priorJobRequest,
      polygonAction: 'none',
      polygonString: null,
      inScope: false,
    };
  }

  const features =
    featureCollection && Array.isArray(featureCollection.features)
      ? featureCollection.features
      : [];
  const hasFeatures = features.length > 0;

  // (Req 2.1, 2.2, 2.3) Location_Method follows the resolvable-Feature count.
  const selectedLocationMethod = hasFeatures ? 'geojson' : 'select';

  // (Req 1.2, 7.x) Shallow-merge polygonInputOptions: set only polygonFormat,
  // preserving polygonNameAliasElement and never adding `geojson`.
  const priorOptions =
    prior.polygonInputOptions && typeof prior.polygonInputOptions === 'object'
      ? prior.polygonInputOptions
      : {};
  const polygonInputOptions = {
    ...priorOptions,
    polygonFormat: 'GEOJSON',
  };

  // (Req 7.1, 7.2) Copy every other field byte-for-byte; overwrite only the
  // touched fields. `polygonStringRef` is carried through unchanged here; the
  // Polygon_Storage side effect (reported below) is what mutates it via the
  // savePolygonString / deletePolygonString actions.
  const jobRequest = {
    ...prior,
    selectedLocationMethod,
    polygonInputOptions,
  };

  return {
    jobRequest,
    polygonAction: hasFeatures ? 'save' : 'delete',
    polygonString: hasFeatures ? JSON.stringify(featureCollection) : null,
    inScope: true,
  };
}
